import { useMeasure } from '@/hooks/useMeasure';
import usePersistFn from '@/hooks/usePersistFn';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type { HttpApiServerType } from '@/services/http-api/httpApi';
import { AppTheme } from '@/shared/constant/AppTheme';
import { Dropdown, Tabs } from 'aui';
import { FoxIconDotsHorizontal } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import styles from './RequestSampleCodeLanguages.module.css';

export type RequestSampleCodeLanguagesProps = {
  languages: HttpApiServerType['codeSampleLanguages'];
  value: string | undefined;
  onChange: (value: string) => void;
  className?: string;
};

export function RequestSampleCodeLanguages(props: RequestSampleCodeLanguagesProps) {
  const { languages, value, onChange, className } = props;

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const themePrimarySettings = useDocsDataModelStore((x) => x.docsBaseConfig.themePrimarySettings);
  const [ref, maxCount] = useMeasure(
    ({ width: containerWidth }) => {
      if (!containerWidth) {
        return 4;
      }

      return parseInt(String((containerWidth - 32) / 70));
    },
    Object.is,
    true,
  );

  const getShowedList = usePersistFn(() => {
    const showedList = languages.slice(0, maxCount);
    const foldedList = languages.slice(maxCount);

    const foldedSelectedIndex = foldedList.findIndex((x) => x.key === value);
    if (foldedSelectedIndex > -1) {
      const foldedSelectedItem = foldedList.splice(foldedSelectedIndex, 1)[0];
      if (foldedSelectedItem) {
        const showListLastItem = showedList.pop();
        showedList.push(foldedSelectedItem);

        if (showListLastItem) {
          foldedList.unshift(showListLastItem);
        }
      }
    }

    return {
      showedList,
      foldedList,
    };
  });

  const [itemList, setItemList] = React.useState(() => {
    return getShowedList();
  });

  React.useEffect(() => {
    setItemList(getShowedList());
  }, [value, languages, getShowedList, maxCount]);

  return (
    <div
      className={clsx('z-[1] flex items-center border-b border-b-[#222A39] px-3', className)}
      data-theme={AppTheme.Dark}
      data-accent-color={themePrimarySettings[AppTheme.Dark].accentColor}
    >
      <div className="ml-1 mr-2 flex-shrink-0 text-base font-semibold text-color-dark">
        <span className="hidden @[850px]/http-api-content:block">
          {clientI18n('request.example.title.right')}
        </span>
        <span className="block @[850px]/http-api-content:hidden">
          {clientI18n('request.example.title.center')}
        </span>
      </div>
      <div className="flex w-0 flex-1 items-center" ref={ref}>
        <Tabs
          activeKey={value ?? itemList.showedList[0]?.key}
          onChange={onChange}
          items={itemList.showedList.map((language) => ({
            key: language.key,
            label: language.label,
          }))}
          className={clsx(styles.tabs)}
          suppressHydrationWarning
        />

        {!!itemList.foldedList.length && (
          <Dropdown
            destroyPopupOnHide
            menu={{
              items: itemList.foldedList.map((x) => {
                return {
                  key: x.key,
                  label: x.label,
                  onClick: () => {
                    onChange(x.key);
                  },
                };
              }),
            }}
          >
            <div className="flex flex-shrink-0 p-1.5 text-base font-500 text-color-secondary-dark">
              <FoxIconDotsHorizontal className="ml-1" size={16} />
            </div>
          </Dropdown>
        )}
      </div>
    </div>
  );
}
